import 'leaflet/dist/leaflet.css';
import './map.css';
import { control, geoJson, GeoJSON, map as leafletMap } from 'leaflet/';
import type { LeafletEvent } from 'leaflet/';
import type { GeometryObject } from 'geojson';
import type { MapData, MapDataFeature } from '../data/mapDataType';
import { MAP_ELEMENT_ID } from '../mapConstants';
import { DATA_MAP_INSTANCE_ID } from './patchMapFrameAndOuterElements';
import { MAP_CITY_CHANGED } from '../mapEvents';
import { type MapOnlineService } from '../../state/createInitialState';
import { mapRegistry } from './mapRegistry';
import type { GeoJSONFixed, MapRegistryEntry } from './mapRegistry';
import type { SimpleStore } from '../../state/SimpleStore';
import { getBoundsZoomUnbounded } from './util/getBoundsZoomUnbounded';
import {
  ODMAP__BUTTON__ZOOM_IN__TITEL,
  ODMAP__BUTTON__ZOOM_OUT__TITEL,
} from '../../texts';
import { resetZoom, ZoomResetControl } from './util/ZoomResetControl';
import { getMapDomIdFromInstance } from './util/getMapDomIdFromInstance';

const KOMMUNE_BORDER = '#ffffff';
const KOMMUNE_COLOR = 'var(--bs-primary)';
const KOMMUNE_ALPHA = 0.1;
const KOMMUNE_ACTIVE_COLOR = 'var(--bs-info)';
const KOMMUNE_ACTIVE_ALPHA = 1;
const KOMMUNE_HOVER_COLOR = 'var(--bs-primary)';
const KOMMUNE_HOVER_ALPHA = 0.5;
const KREIS_BORDER = '#ff0000';
const KREIS_COLOR = 'var(--bs-primary)';
const KREIS_ALPHA = 0.1;
const KREIS_ACTIVE_COLOR = 'var(--bs-info)';
const KREIS_ACTIVE_ALPHA = 0.5;
const KREIS_HOVER_COLOR = 'var(--bs-info)';
const KREIS_HOVER_ALPHA = 0.5;

/**
 * Styleeinstellungen für Kreis-Layer der Karte
 */
const STYLE_LANDKREIS = {
  styleName: 'styleLandkreise',
  fillColor: KREIS_COLOR,
  weight: 0.3,
  opacity: 0.5,
  color: KREIS_BORDER,
  dashArray: '',
  fillOpacity: KREIS_ALPHA,
};

/**
 * Styleeinstellungen für Kommune-Layer der Karte
 */
const STYLE_KOMMUNE = {
  styleName: 'styleKommunen',
  fillColor: KOMMUNE_COLOR,
  weight: 1.5,
  opacity: 0.5,
  color: KOMMUNE_BORDER,
  dashArray: '2',
  fillOpacity: KOMMUNE_ALPHA,
};

/**
 * Styleeinstellungen für den aktiven Kreis-Layer der Karte
 */
const KREIS_STYLE_ACTIVE = {
  color: KREIS_BORDER,
  fillColor: KREIS_ACTIVE_COLOR,
  fillOpacity: KREIS_ACTIVE_ALPHA,
};

/**
 * Styleeinstellungen für den aktiven Kommune-Layer der Karte
 */
const KOMMUNE_STYLE_ACTIVE = {
  color: KOMMUNE_BORDER,
  fillColor: KOMMUNE_ACTIVE_COLOR,
  fillOpacity: KOMMUNE_ACTIVE_ALPHA,
};

let hmrLastRenderedStore: SimpleStore | undefined;

/**
 * Merkt sich den Store, der für das letzte Rendern verwendet wurde. Wird benötigt, um Events an den Store weiterzugeben
 * @param {SimpleStore} store
 */
export function setLastRenderedStore(store: SimpleStore) {
  hmrLastRenderedStore = store;
}

/**
 * Die Leaflet-Karte mit der ID mapDomId auf Basis des mapState aktualisieren.
 *
 * Z.B. wird bei einem eventuell geänderten Regionalschlüssel das Highlighting
 * angepasst. Außerdem wird geprüft, ob sich die Größe der Karte geändert hat
 * (weil sich die Größe des Browserfensters geändert hat).
 *
 * @param {string} mapDomId Id der zu aktualisierenden Karte
 * @param {MapOnlineService} mapState
 * @returns {string | undefined}
 */
export function updateLeafletMap(
  mapDomId: string,
  mapState: MapOnlineService,
): string | undefined {
  // Hole die Instanz-ID der Karte
  const mapInstanceId =
    (
      document.getElementById(MAP_ELEMENT_ID(mapDomId)) ?? undefined
    )?.getAttribute(DATA_MAP_INSTANCE_ID) ?? undefined;
  if (!mapInstanceId) {
    console.error(`no map instance id for ${mapDomId}`);
    return undefined;
  }

  // Internen Zustand und Leaflet-Referenzen für Karten-Instanz holen
  const mapRegistryEntry = mapRegistry.entries.get(mapInstanceId);
  if (!mapRegistryEntry) {
    console.error(`mapRegistryEntry for ${mapInstanceId} is missing`);
    return undefined;
  }

  // invalidateSize() prüft auf Änderung der Containergröße und passt bei Bedarf das Rendering an
  mapRegistryEntry.map.invalidateSize();

  // Falls von außerhalb der selektierte Regionalschlüssel geändert wurde, aktiviere/deaktiviere das Highlighting des entsprechenden Kreises/Kommune
  const regschl = mapState.selectedRegschl;
  if (mapRegistryEntry.renderedRegschl !== regschl) {
    const renderedKreis = mapRegistryEntry.renderedRegschl?.substring(0, 5);
    const kreis = regschl.substring(0, 5);

    const kreisLayer = mapRegistryEntry.regschlToLayer.get(kreis);
    if (renderedKreis !== kreis) {
      if (kreisLayer) {
        selectLayer(mapRegistryEntry, kreisLayer);
      } else {
        unselectLandkreis(mapRegistryEntry);
      }
    }
    const kommuneLayer = mapRegistryEntry.regschlToLayer.get(regschl);
    if (kommuneLayer) {
      selectLayer(mapRegistryEntry, kommuneLayer);
    } else {
      unselectKommune(mapRegistryEntry);
    }

    if (!kreisLayer && !kommuneLayer) {
      resetZoom(mapRegistryEntry.map);
    }

    mapRegistryEntry.renderedRegschl = regschl;
  }

  return mapInstanceId;
}

/**
 * Erzeugt einen Event-Handler für das MouseOver-Event über den Layern (Kreis oder Kommune) der Karte. Sorgt für das Highlighting des gehoverten Layers.
 */
function handleLayerMouseOver(mapInstanceId: string) {
  return (e: LeafletEvent) => {
    const layer = e.target as GeoJSONFixed;

    const mapRegistryEntry = mapRegistry.entries.get(mapInstanceId);
    if (!mapRegistryEntry) {
      console.error(`No map registry entry for ${mapInstanceId}`);
      return;
    }

    if (mapRegistryEntry.hoveredLayer === layer) {
      // der Layer ist bereits markiert
      return;
    }

    if (mapRegistryEntry.hoveredLayer) {
      resetHighlightForLayer(mapRegistryEntry, mapRegistryEntry.hoveredLayer);
    }
    mapRegistryEntry.hoveredLayer = layer;

    if (layer.options.styleName === 'styleLandkreise') {
      layer.setStyle({
        color: KREIS_BORDER,
        fillColor: KREIS_HOVER_COLOR,
        fillOpacity: KREIS_HOVER_ALPHA,
      });
    } else {
      layer.setStyle({
        color: KOMMUNE_BORDER,
        fillColor: KOMMUNE_HOVER_COLOR,
        fillOpacity: KOMMUNE_HOVER_ALPHA,
      });
    }

    const { feature } = layer;
    if (feature?.type === 'Feature') {
      const elementName = feature.properties.GEN;
      layer
        .bindTooltip(elementName, {
          className:
            'vwp-map-tooltip font-font-roc-grotesk-w05-regular text-light bg-dark',
        })
        .openTooltip();
    }
  };
}

/**
 * Setzt das Highlighting des angegebenen Layers zurück.
 */
function resetHighlightForLayer(
  mapRegistryEntry: MapRegistryEntry,
  layer: GeoJSONFixed,
) {
  // closeTooltip reicht nicht, da beim Ziehen bis zum Rand noch gebundene Tooltips wieder angezeigt aber nicht geschlossen werden (mit leaflet 1.9.4)
  layer.unbindTooltip();

  if (layer.options.styleName === 'styleLandkreise') {
    if (layer === mapRegistryEntry.activeLandkreis) {
      layer.setStyle(KREIS_STYLE_ACTIVE);
    } else {
      mapRegistryEntry.layerLandkreise.resetStyle(layer);
    }
  }

  if (layer.options.styleName === 'styleKommunen') {
    if (layer === mapRegistryEntry.activeKommune) {
      layer.setStyle(KOMMUNE_STYLE_ACTIVE);
    } else {
      mapRegistryEntry.layerKommunen.resetStyle(layer);
    }
  }

  if (layer === mapRegistryEntry.hoveredLayer) {
    // eslint-disable-next-line no-param-reassign
    mapRegistryEntry.hoveredLayer = undefined;
  }
}

/**
 * Erzeugt einen Event-Handler für das MouseOut-Event der Karten-Layer (Kreis/Kommune). Sorgt für das Entfernen des Highlightings des Layers.
 */
function handleLayerMouseOut(mapInstanceId: string) {
  return (e: LeafletEvent) => {
    const layer = e.target as GeoJSONFixed;

    const mapRegistryEntry = mapRegistry.entries.get(mapInstanceId);
    if (!mapRegistryEntry) {
      console.error(`No map registry entry for ${mapInstanceId}`);
      return;
    }
    resetHighlightForLayer(mapRegistryEntry, layer);
  };
}

/**
 * Liefert den Style für Kommunelayer zurück, falls das angegebene Feature eine Kommune (und kein Kreis) ist.
 */
function styleKommunen(feature: MapDataFeature | undefined) {
  switch (feature?.properties.SN_L) {
    case '06':
      return STYLE_KOMMUNE;
    default:
      return {};
  }
}

/**
 * Liefert den Style für Kreislayer zurück, falls das angegebene Feature ein Kreis (und keine Kommune) ist.
 */
function styleLandkreise(feature: MapDataFeature | undefined) {
  switch (feature?.properties.SN_L) {
    case '06':
      return STYLE_LANDKREIS;
    default:
      return {};
  }
}

/**
 * Deselektiert den aktuell aktiven Kommunelayer (falls einer aktiv ist).
 */
function unselectKommune(mapRegistryEntry: MapRegistryEntry) {
  if (mapRegistryEntry.activeKommune) {
    mapRegistryEntry.layerKommunen.resetStyle(mapRegistryEntry.activeKommune);
    // eslint-disable-next-line no-param-reassign
    mapRegistryEntry.activeKommune = undefined;
  }
}

/**
 * Deselektiert den aktuell aktiven Kreislayer (falls einer aktiv ist).
 */
function unselectLandkreis(mapRegistryEntry: MapRegistryEntry) {
  if (mapRegistryEntry.activeLandkreis) {
    mapRegistryEntry.layerLandkreise.resetStyle(
      mapRegistryEntry.activeLandkreis,
    );
    mapRegistryEntry.activeLandkreis.bringToFront();
    // eslint-disable-next-line no-param-reassign
    mapRegistryEntry.activeLandkreis = undefined;
  }
}

/**
 * Selektiert der angegebenen Layer (Kreis oder Kommune), sodass dieser
 *  anschließend aktiv ist. Falls bereits ein anderer Kreis oder eine andere
 *  Kommune aktiv sind, so wird diese deselektiert wird, sodass maximal ein
 *  Kreis und eine Kommune aktiv sind.
 */
function selectLayer(mapRegistryEntry: MapRegistryEntry, layer: GeoJSONFixed) {
  unselectKommune(mapRegistryEntry);

  if (layer.options.styleName === 'styleLandkreise') {
    layer.setStyle(KREIS_STYLE_ACTIVE);
    unselectLandkreis(mapRegistryEntry);
    // eslint-disable-next-line no-param-reassign
    mapRegistryEntry.activeLandkreis = layer;

    mapRegistryEntry.map.fitBounds(layer.getBounds());

    layer.bringToBack();
  } else {
    layer.setStyle(KOMMUNE_STYLE_ACTIVE);

    // eslint-disable-next-line no-param-reassign
    mapRegistryEntry.activeKommune = layer;
  }

  const { feature } = layer;
  if (feature?.type === 'Feature') {
    const title = feature.properties.BEZ;
    const name = feature.properties.GEN;
    const regschl = feature.properties.RS;
    const online = Math.trunc(Math.random() * 200);
    const nameElem = document.getElementById('vwp-map-name');
    const regschlElem = document.getElementById('vwp-map-regschl');
    const onlElem = document.getElementById('vwp-map-onlinedienste');
    if (nameElem && regschlElem && onlElem) {
      nameElem.textContent = `${title}: ${name}`;
      regschlElem.textContent = `Regionalschlüssel: ${regschl}`;
      onlElem.textContent = `Online-Dienste: ${online}`;
    }
  }
}

/**
 * Erzeuqt einen Eventhandler für das Klick-Event eines Layers. Sorgt dafür,
 * dass der entsprechende Kreis oder Kommune aktiv wird. Gibt auch einen Event
 * an den Store weiter.
 */
function handleLayerClicked(mapInstanceId: string) {
  return (e: LeafletEvent) => {
    const layer = e.target as GeoJSON<
      MapDataFeature['properties'],
      GeometryObject
    > & {
      options: { styleName: string };
    };

    if (layer.feature?.type === 'Feature' && hmrLastRenderedStore) {
      const mapDomId = getMapDomIdFromInstance(mapInstanceId);
      if (!mapDomId) {
        console.error('Invalid instance id', mapInstanceId);
        return;
      }
      hmrLastRenderedStore.dispatch(
        MAP_CITY_CHANGED({
          mapDomId,
          regschl: layer.feature.properties.RS_0,
        }),
      );
    }
  };
}

/**
 * Erzeugt eine Funktion, die die nötigen Eventhandler an die Layer setzt.
 */
function addEventHandlersToFeatures(
  mapInstanceId: string,
  regschlToLayer: Map<string, GeoJSONFixed>,
) {
  return (
    _feature: unknown,
    layer: GeoJSON<MapDataFeature['properties'], GeometryObject>,
  ) => {
    layer.on({
      mouseover: handleLayerMouseOver(mapInstanceId),
      mouseout: handleLayerMouseOut(mapInstanceId),
      click: handleLayerClicked(mapInstanceId),
    });
    const { feature } = layer;
    if (feature?.type === 'Feature') {
      regschlToLayer.set(feature.properties.RS, layer);
    }
  };
}

/**
 * Nötig, damit sich die Karte an die Elementgröße anpasst
 */
const resizeObserver = new ResizeObserver((entries) => {
  entries.forEach((entry) => {
    const elem = entry.target;
    const mapInstanceId = elem.getAttribute(DATA_MAP_INSTANCE_ID);

    if (!mapInstanceId) {
      console.error('resizeObserver: element has no map id', elem);
      return;
    }
    const mapRegistryEntry = mapRegistry.entries.get(mapInstanceId);

    if (!mapRegistryEntry) {
      console.error(`resizeObserver: no registry entry for ${mapInstanceId}`);
      return;
    }

    const previousMinZoom = mapRegistryEntry.map.getMinZoom();
    const previousZoom = mapRegistryEntry.map.getZoom();
    mapRegistryEntry.map.invalidateSize();
    const minZoom = getBoundsZoomUnbounded(
      mapRegistryEntry.map,
      mapRegistryEntry.layerKommunen.getBounds(),
    );
    mapRegistryEntry.map.setMinZoom(minZoom);
    if (previousZoom === previousMinZoom && minZoom < previousMinZoom) {
      mapRegistryEntry.map.setZoom(minZoom);
    }
  });
});

/**
 * Fügt eine Leafletkarte in das angegebene Dom-Element ein.
 */
export function insertLeafletMap(
  mapElement: HTMLDivElement,
  kommunen: MapData,
  landkreise: MapData,
) {
  const mapInstanceId = mapElement.getAttribute(DATA_MAP_INSTANCE_ID);
  if (!mapInstanceId) {
    console.error("Map element doesn't have map instance id", mapElement);
    return;
  }

  // horche auf Größenveränderungen der Karte
  resizeObserver.observe(mapElement);

  const regschlToLayer = new Map<string, GeoJSONFixed>();

  const layerKommunen = geoJson(kommunen, {
    style: styleKommunen,
    onEachFeature: addEventHandlersToFeatures(mapInstanceId, regschlToLayer),
  });

  const layerLandkreise = geoJson(landkreise, {
    style: styleLandkreise,
    onEachFeature: addEventHandlersToFeatures(mapInstanceId, regschlToLayer),
  });

  const map = leafletMap(mapElement, {
    zoomControl: false,
    dragging: true,
    maxZoom: 9,
    center: [0, 0],
    zoom: 12,
    zoomSnap: 0,
    layers: [layerKommunen, layerLandkreise],
    maxBounds: layerKommunen.getBounds(),
    maxBoundsViscosity: 0.8,
    // minZoom: 8,
  });
  control
    .zoom({
      position: 'bottomright',
      zoomInTitle: ODMAP__BUTTON__ZOOM_IN__TITEL,
      zoomOutTitle: ODMAP__BUTTON__ZOOM_OUT__TITEL,
    })
    .addTo(map);
  new ZoomResetControl({}).addTo(map);

  map.fitBounds(layerKommunen.getBounds());
  map.setMinZoom(map.getBoundsZoom(layerKommunen.getBounds()));

  const mapRegistryEntry: MapRegistryEntry = {
    layerKommunen,
    layerLandkreise,
    map,
    regschlToLayer,
  };
  mapRegistry.entries.set(mapInstanceId, mapRegistryEntry);

  /*
  tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
    maxZoom: 19,
    attribution:
      '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
  }).addTo(m);
   */
}

/**
 * Entfernt die internen Zustände für alle Karteninstanzen außer den hier angegebenen noch aktiven Instanzen
 */
export function cleanupLeafletMapInstances(existingInstances: string[]) {
  [...mapRegistry.entries.entries()].forEach(([key, entry]) => {
    if (!existingInstances.includes(key)) {
      mapRegistry.entries.delete(key);
      entry.map.remove();
    }
  });
}
